import { AfterContentInit, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ILookUpType, ISavedAddressDtoModel } from 'lib-core-shared';
import { AddressUtilities, ENVIRONMENT, Environment, ICountryDtoModel, RolePermissionService, UtilitiesService, LocationService } from 'lib-core-shared';
import { ISavedAddressModel } from 'lib-core-shared';
import { ISavedAddressPostDtoModel } from 'lib-core-shared';
import { SavedAddressService } from 'lib-core-shared';
import { ResolveCustomerAddressMapping } from './resolve-customer-address.mapping';
import { switchMap } from 'rxjs/operators';

enum State {
  NewAddress, IncompleteAddress, SelectDefault, Loading, Error
}

@Component({
  selector: 'app-resolve-customer-address',
  templateUrl: './resolve-customer-address.component.html',
  styleUrls: ['./resolve-customer-address.component.scss']
})
export class ResolveCustomerAddressComponent implements OnInit {
  state: State = State.Loading;
  form!: FormGroup;
  @Output() successEmitter = new EventEmitter<ISavedAddressModel>();
  @Output() closeEmitter = new EventEmitter<string>();
  public submitButtonDisabled = false;
  isBusy!: boolean;
  @Input() customerId!: string;
  @Input() defaultAddress!: ISavedAddressModel | undefined;
  public savedAddresses: ISavedAddressModel[] = [];
  fb: any;
  private validatorAdresses: ISavedAddressModel[] = [];
  mapping!: ResolveCustomerAddressMapping;
  countries: ICountryDtoModel[] = [];
  formSubmitted: boolean = false;
  isAddressVerifiedOrOverruled: boolean = false;
  constructor(
    @Inject(ENVIRONMENT) private env: Environment,
    private readonly SavedAddressService: SavedAddressService,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public modalService: NgbModal,
    public utilitiesService: UtilitiesService,
    public roleService: RolePermissionService,
    private addressUtils: AddressUtilities,
    private locationService: LocationService
  ) {
  }

  public addressDetails(): FormGroup {
    return this.form.controls.details as FormGroup;
  }

  ngOnInit(): void {

    this.locationService.getAllCountries()
      .pipe(
        switchMap((countries: ICountryDtoModel[]) => {
          this.countries = countries;
          return this.SavedAddressService.getCustomerAddresses(this.customerId)
        })
      ).subscribe((savedAddresses: ISavedAddressModel[]) => {

        //if there are saved addresses
        if (savedAddresses.length > 0) {

          let defaultAddress = savedAddresses.filter((x) => x.details.isDefault == true)[0];

          //if there is a default address
          if (defaultAddress != undefined) {
            //but the address details are incomplete
            if (this.addressUtils.isSavedAddressComplete(defaultAddress) == false) {
              //present a form to complete the address details
              this.fillAnIncompleteDefaultAddressForm(defaultAddress);
            } else {
              //emit the default address
              this.successEmitter.emit(defaultAddress);
            }
            //if there's no default address
          } else {
            //present a form to select a default from the saved addresses
            this.selectAddressAsDefaultForm(savedAddresses)
          }
          //if there's no saved address
        } else {
          //present form to create a new address
          this.createNewAddressForm();
        }

      }, (error) => {
        this.state = State.Error
        this.fb = {
          text: `${error.status} : ${error.statusText ? error.statusText : 'An error occurred'}`,
          error: error
        };
      });

    this.mapping = new ResolveCustomerAddressMapping();
  }

  selectAddressAsDefaultForm(addresses: ISavedAddressModel[]): void {
    this.state = State.SelectDefault;
    this.savedAddresses = addresses;
    this.form = new FormGroup({
      defaultAddress: new FormControl(addresses[0], Validators.required),
    })

    if(addresses.length == 1){
      this.saveNewDefaultAddress(this.form)
    }
  }

  fillAnIncompleteDefaultAddressForm(defaultAddress: ISavedAddressModel): void {
    console.log(defaultAddress);

    this.state = State.IncompleteAddress;
    this.form = new FormGroup({
      id: new FormControl(defaultAddress.id),
      details: new FormGroup({
        name: new FormControl(defaultAddress.details.name, Validators.required),
        addressLine1: new FormControl(defaultAddress.details.addressLine1, Validators.required),
        addressLine2: new FormControl(defaultAddress.details.addressLine2),
        townCity: new FormControl(defaultAddress.details.townCity),
        countyState: new FormControl(defaultAddress.details.countyState),
        postalCode: new FormControl(defaultAddress.details.postalCode, Validators.required),
        country: new FormControl(defaultAddress.details.country, Validators.required),
        isDefault: new FormControl(defaultAddress.details.isDefault),
        isBillTo: new FormControl(defaultAddress.details.isBillTo),
        isBillToDefault: new FormControl(defaultAddress.details.isBillToDefault),
        isResidentialAddress: new FormControl(defaultAddress.details.isResidentialAddress),
        isShipTo: new FormControl(defaultAddress.details.isShipTo),
        isShipToDefault: new FormControl(defaultAddress.details.isShipToDefault),
        isVerified: new FormControl(defaultAddress.details.isVerified),
        contactNumber: new FormControl(defaultAddress.details.contactNumber, Validators.required),
      })
    })
  }

  createNewAddressForm(): void {
    this.state = State.NewAddress;
    this.form = new FormGroup({
      name: new FormControl(null, Validators.required),
      addressLine1: new FormControl(null, Validators.required),
      addressLine2: new FormControl(null),
      townCity: new FormControl(null),
      countyState: new FormControl(null),
      postalCode: new FormControl(null, Validators.required),
      country: new FormControl(null, Validators.required),
      isBillTo: new FormControl(false),
      isDefault: new FormControl(true),
      isResidentialAddress: new FormControl(false),
      isBillToDefault: new FormControl(false),
      isShipTo: new FormControl(false),
      isShipToDefault: new FormControl(false),
      isVerified: new FormControl(false),
      contactNumber: new FormControl(null, Validators.required),
    })
  }

  get canDefault(): boolean {

    let ctrl = this.form.controls?.isDefault ? this.form.controls?.isDefault : this.addressDetails().controls?.isDefault;

    const value = ctrl.value ? ctrl.value as boolean : false;
    return value && this.validatorAdresses.length ? this.validatorAdresses.filter(x => x.details.isDefault).length === 0 : true;
  }

  get canShipTo(): boolean {

    let shipToCtrl = this.form.controls?.isShipTo ? this.form.controls?.isShipTo : this.addressDetails().controls?.isShipTo;
    let defShipToCtrl = this.form.controls?.isShipToDefault ? this.form.controls?.isShipToDefault : this.addressDetails().controls?.isShipToDefault;

    if (!shipToCtrl.value) {
      defShipToCtrl.setValue(false);
      defShipToCtrl.disable();
      return true;
    }
    const value = defShipToCtrl.value ? defShipToCtrl.value as boolean : false;
    return value && this.validatorAdresses.length ? this.validatorAdresses.filter(x => x.details.isShipToDefault).length === 0 : true;
  }

  get canBillTo(): boolean {

    let billToCtrl = this.form.controls?.isBillTo ? this.form.controls?.isBillTo : this.addressDetails().controls?.isBillTo;
    let defBillToCtrl = this.form.controls?.isBillToDefault ? this.form.controls?.isBillToDefault : this.addressDetails().controls?.isBillToDefault;

    if (!billToCtrl.value) {
      defBillToCtrl.setValue(false);
      defBillToCtrl.disable();
      return true;
    }
    const value = defBillToCtrl.value ? defBillToCtrl.value as boolean : false;
    return value && this.validatorAdresses.length ? this.validatorAdresses.filter(x => x.details.isBillToDefault).length === 0 : true;
  }


  public save(form: FormGroup) {

    this.form.markAllAsTouched();
    this.formSubmitted = true;
    if (this.form.valid && this.isAddressVerifiedOrOverruled) {
      this.state = State.Loading;
      const model: ISavedAddressPostDtoModel = this.mapping.toApplicationPostModel(form, this.customerId);

      this.SavedAddressService.save(this.customerId, model)
        .subscribe((r: ISavedAddressModel) => {
          this.successEmitter.emit(r);
        },
          (error) => {
            this.state = State.NewAddress;
            this.fb = {
              text: `${error.status} : ${error.statusText ? error.statusText : 'An error occurred'}`,
              error: error
            };
          })


    }
  }

  public saveUpdatedAddress(form: FormGroup) {
    this.form.markAllAsTouched();
    this.formSubmitted = true;
    if (this.form.valid && this.isAddressVerifiedOrOverruled) {
      this.state = State.Loading;
      const model: ISavedAddressDtoModel = this.mapping.toApplicationPutModel(form);

      this.SavedAddressService.update(this.customerId, model)
        .subscribe((r: ISavedAddressModel) => {
          this.successEmitter.emit(r);
        },
          (error) => {
            this.formSubmitted = false;
            this.state = State.IncompleteAddress;
            this.fb = {
              text: `${error.status} : ${error.statusText ? error.statusText : 'An error occurred'}`,
              error: error
            };
          })


    }
  }

  public saveNewDefaultAddress(form: FormGroup) {
    this.form.markAllAsTouched();
    this.formSubmitted = true;
    if (this.form.valid) {
      this.state = State.Loading;
      const model: ISavedAddressDtoModel = this.mapping.toApplicationPutModelFromDefaultAddress(form);

      model.details.isDefault = true;

      this.SavedAddressService.update(this.customerId, model)
        .subscribe((r: ISavedAddressModel) => {
          this.successEmitter.emit(r);
        },
          (error:any) => {
            this.state = State.SelectDefault;
            this.fb = {
              text: `${error.status} : ${error.statusText ? error.statusText : 'An error occurred'}`,
              error: error
            };
          })


    }
  }

  public setCountrySelected(country: ICountryDtoModel) {
    console.log('country', country)
    if (this.state == State.IncompleteAddress) {
      this.addressDetails().controls.country.setValue(country);
    }
    if (this.state == State.NewAddress) {
      this.form.controls.country.setValue(country);

    }
  }

  public updateIsAddressVerifiedOrOverruledState(e: any, form: FormGroup){

    this.isAddressVerifiedOrOverruled = e;

    //the search address component has told us the address is verified or overruled by user which is triggered when the parent form is submitted, so we can submit the form automatically here
    if(e == true){

      if(this.state == State.IncompleteAddress){
        this.saveUpdatedAddress(form)
      }

      if(this.state == State.NewAddress){
        this.save(form)
      }

    }
  }
}
